import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useUserAccounts } from './UserAccountsContext';
import { useAuth } from './AuthenticationContext';
import { getSupabase } from '@trader-dashboard/database';
// Group events within this time window (ms)
export const GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
// Target minimum events per page
const PAGE_SIZE = 20;
// Max events to pull while trying to close a boundary group (safety cap)
const MAX_BOUNDARY_FETCH = 200;
const EventsContext = createContext(undefined);
export const useEvents = () => {
    const context = useContext(EventsContext);
    if (!context) {
        throw new Error('useEvents must be used within EventsProvider');
    }
    return context;
};
const getSymbol = (event) => event.payload.symbol?.replace('.PRO', '') ?? 'Unknown';
export const getNetProfit = (event) => (event.payload.profit ?? 0) + (event.payload.commission ?? 0) + (event.payload.swap ?? 0);
/**
 * Group events by symbol + type within GROUP_WINDOW_MS.
 * Events are expected to be ordered newest-first.
 */
const groupEvents = (events) => {
    const groups = [];
    for (const event of events) {
        const symbol = getSymbol(event);
        const eventTime = new Date(event.created_at).getTime();
        // Find an existing group for this symbol+type whose time range this event can extend
        const existingGroup = groups.find((g) => g.symbol === symbol &&
            g.eventType === event.type &&
            Math.abs(new Date(g.oldestTime).getTime() - eventTime) < GROUP_WINDOW_MS);
        if (existingGroup) {
            existingGroup.events.push(event);
            existingGroup.totalProfit += getNetProfit(event);
            if (event.created_at > existingGroup.latestTime) {
                existingGroup.latestTime = event.created_at;
            }
            if (event.created_at < existingGroup.oldestTime) {
                existingGroup.oldestTime = event.created_at;
            }
        }
        else {
            groups.push({
                key: `${symbol}-${event.type}-${event.id}`,
                symbol,
                eventType: event.type,
                events: [event],
                totalProfit: getNetProfit(event),
                latestTime: event.created_at,
                oldestTime: event.created_at,
            });
        }
    }
    return groups;
};
/**
 * Check if a candidate event would join the last group in the list.
 * Used to detect when we need to keep fetching to close a boundary group.
 */
const wouldJoinLastGroup = (groups, candidate) => {
    if (groups.length === 0)
        return false;
    const last = groups[groups.length - 1];
    const candidateSymbol = getSymbol(candidate);
    const candidateTime = new Date(candidate.created_at).getTime();
    const lastOldestTime = new Date(last.oldestTime).getTime();
    return (last.symbol === candidateSymbol &&
        last.eventType === candidate.type &&
        Math.abs(lastOldestTime - candidateTime) < GROUP_WINDOW_MS);
};
export const EventsProvider = ({ children }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [hasMore, setHasMore] = useState(true);
    const [cachedAccountId, setCachedAccountId] = useState(null);
    const [recentCounts, setRecentCounts] = useState({
        last6Hours: 0,
        last24Hours: 0,
        hourlyBuckets: new Array(24).fill(0),
    });
    const [recentCountsLoading, setRecentCountsLoading] = useState(false);
    const { selectedAccountId } = useUserAccounts();
    const { authenticated } = useAuth();
    /**
     * Fetch non-import events from the last 24 hours, apply the same grouping
     * used in the feed (symbol + type within GROUP_WINDOW_MS), then derive
     * counts and an hourly histogram from the grouped data. This way the
     * metric number and the bars reflect what the user actually sees in the feed.
     */
    const fetchRecentCounts = useCallback(async (accountId) => {
        setRecentCountsLoading(true);
        try {
            const supabase = getSupabase();
            const now = Date.now();
            const sixHoursAgoMs = now - 6 * 60 * 60 * 1000;
            const twentyFourHoursAgoMs = now - 24 * 60 * 60 * 1000;
            const twentyFourHoursAgoIso = new Date(twentyFourHoursAgoMs).toISOString();
            const { data, error: fetchError } = await supabase
                .from('events')
                .select('*')
                .eq('account_id', accountId)
                .neq('source', 'import')
                .gte('created_at', twentyFourHoursAgoIso)
                .order('created_at', { ascending: false })
                .order('id', { ascending: false });
            if (fetchError)
                throw new Error(fetchError.message);
            const rows = (data ?? []);
            // Group the same way as the feed so the counts match.
            const groupedRows = groupEvents(rows);
            // Bucket groups into 24 hourly bins. Index 0 = 23-24h ago, index 23 = last hour.
            const hourlyBuckets = new Array(24).fill(0);
            let last6Hours = 0;
            for (const group of groupedRows) {
                // Use the group's latestTime as the representative timestamp
                const t = new Date(group.latestTime).getTime();
                if (t < twentyFourHoursAgoMs)
                    continue;
                if (t >= sixHoursAgoMs)
                    last6Hours++;
                const hoursAgo = Math.floor((now - t) / (60 * 60 * 1000));
                const bucketIndex = 23 - Math.min(23, Math.max(0, hoursAgo));
                hourlyBuckets[bucketIndex]++;
            }
            setRecentCounts({
                last6Hours,
                last24Hours: groupedRows.length,
                hourlyBuckets,
            });
        }
        catch (err) {
            console.error('Events recent counts fetch error:', err);
        }
        finally {
            setRecentCountsLoading(false);
        }
    }, []);
    /**
     * Fetch a page of events, then keep fetching extra events as long as
     * they would join the last group. This guarantees we never show a
     * partial group at the boundary.
     */
    const fetchEventsPage = useCallback(async (accountId, offset, append) => {
        setLoading(true);
        setError(undefined);
        try {
            const supabase = getSupabase();
            // Step 1: fetch initial batch of PAGE_SIZE events
            const { data: initial, error: fetchError } = await supabase
                .from('events')
                .select('*')
                .eq('account_id', accountId)
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .range(offset, offset + PAGE_SIZE - 1);
            if (fetchError)
                throw new Error(fetchError.message);
            const collected = (initial ?? []);
            let gotFullPage = collected.length === PAGE_SIZE;
            let cursor = offset + collected.length;
            // Step 2: if we got a full page, check if the boundary group is still open
            // by peeking at subsequent events and pulling them while they extend the last group.
            if (gotFullPage) {
                while (cursor - offset < MAX_BOUNDARY_FETCH) {
                    // Peek at a small batch to see if the boundary group needs closing
                    const peekSize = 10;
                    const { data: peek, error: peekError } = await supabase
                        .from('events')
                        .select('*')
                        .eq('account_id', accountId)
                        .order('created_at', { ascending: false })
                        .order('id', { ascending: false })
                        .range(cursor, cursor + peekSize - 1);
                    if (peekError)
                        throw new Error(peekError.message);
                    const peekEvents = (peek ?? []);
                    if (peekEvents.length === 0) {
                        gotFullPage = false; // no more events — nothing left to fetch
                        break;
                    }
                    // Build the groups so far (collected + peek) to check boundary state
                    const tentativeGroups = groupEvents(collected);
                    const firstPeek = peekEvents[0];
                    if (!wouldJoinLastGroup(tentativeGroups, firstPeek)) {
                        // Boundary is safe — do NOT include peeked events in this page
                        break;
                    }
                    // The first peek event joins the last group. Pull events from peek
                    // as long as they keep extending it, then stop.
                    let addedCount = 0;
                    for (const e of peekEvents) {
                        const groupsNow = groupEvents(collected);
                        if (wouldJoinLastGroup(groupsNow, e)) {
                            collected.push(e);
                            addedCount++;
                        }
                        else {
                            break;
                        }
                    }
                    cursor += addedCount;
                    // If we consumed the entire peek batch, we might still be inside the group
                    // and need to keep peeking. Otherwise, stop.
                    if (addedCount < peekEvents.length) {
                        break;
                    }
                    // Defensive: if we pulled less than peekSize we're at the end
                    if (peekEvents.length < peekSize) {
                        gotFullPage = false;
                        break;
                    }
                }
            }
            setHasMore(gotFullPage);
            if (append) {
                // Offsets shift when new events arrive between pages; drop any overlap.
                setEvents(prev => {
                    const seen = new Set(prev.map(e => e.id));
                    return [...prev, ...collected.filter(e => !seen.has(e.id))];
                });
            }
            else {
                setEvents(collected);
            }
        }
        catch (err) {
            console.error('Events fetch error:', err);
            setError(err.message || 'Failed to load events');
        }
        finally {
            setLoading(false);
        }
    }, []);
    const refresh = useCallback(async () => {
        if (!selectedAccountId)
            return;
        setCachedAccountId(selectedAccountId);
        setHasMore(true);
        await Promise.all([
            fetchEventsPage(selectedAccountId, 0, false),
            fetchRecentCounts(selectedAccountId),
        ]);
    }, [selectedAccountId, fetchEventsPage, fetchRecentCounts]);
    const loadMore = useCallback(async () => {
        if (!selectedAccountId || loading || !hasMore)
            return;
        await fetchEventsPage(selectedAccountId, events.length, true);
    }, [selectedAccountId, loading, hasMore, events.length, fetchEventsPage]);
    useEffect(() => {
        if (!authenticated)
            return;
        if (!selectedAccountId)
            return;
        if (selectedAccountId === cachedAccountId && events.length > 0)
            return;
        refresh();
    }, [authenticated, selectedAccountId, cachedAccountId, events.length, refresh]);
    const groups = useMemo(() => groupEvents(events), [events]);
    return (_jsx(EventsContext.Provider, { value: { events, groups, loading, error, hasMore, loadMore, refresh, recentCounts, recentCountsLoading }, children: children }));
};
